import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, CircularProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Header from '../../components/Header';
import { apiGet, apiDelete } from '../../utils/apiUtils';
import { API_BASE_URL } from '../../config/api';
import { getUserData } from '../../services/authService';

const InvestListingPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    
    // State for API data and loading
    const [investorData, setInvestorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    
    // Modal state
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [investorToDelete, setInvestorToDelete] = useState(null);

    // Fetch data from API
    useEffect(() => {
        const fetchInvestorData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await apiGet(`/api/investor/getAll-investor?page=${page}&pageSize=${pageSize}`);
                
                if (response.success) {
                    setInvestorData(response.result.data || []);
                    setTotalPages(response.result.totalPages || 1);
                    setTotalRecords(response.result.totalRecords || 0);
                } else {
                    setError(response.message || 'Failed to fetch investor data');
                    setInvestorData([]);
                }
            } catch (err) {
                setError('An error occurred while fetching data');
                console.error('Error fetching investor data:', err);
            } finally {
                setLoading(false);
            }
        };
        
        fetchInvestorData();
    }, [page, pageSize]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Open view modal
    const openViewModal = (investor) => {
        setSelectedInvestor(investor);
        setCurrentImageIndex(0);
        setIsViewModalOpen(true);
    };

    // Close view modal
    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedInvestor(null);
    };

    // Handle delete investor (opens confirmation modal)
    const handleDeleteInvestor = (investor) => {
        setInvestorToDelete(investor);
        setIsDeleteModalOpen(true);
    };

    // Handle confirmation of delete
    const confirmDelete = async () => {
        if (!investorToDelete) return;
        try {
            // Use POST request with JSON body as per the API specification
            const response = await fetch(`${API_BASE_URL}/api/investor/deleteInvestor`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add any other necessary headers like authorization tokens here
                    // 'Authorization': `Bearer ${yourAuthToken}`,
                },
                body: JSON.stringify({
                    Investor_ID: investorToDelete.Investor_ID
                }),
            });

            const result = await response.json();

            if (response.ok && result.success) {
                // Refresh the data by filtering out the deleted investor
                const updatedData = investorData.filter(investor => investor.Investor_ID !== investorToDelete.Investor_ID);
                setInvestorData(updatedData);
                setTotalRecords(prev => prev - 1);
            } else {
                // Handle API errors even if response.ok is true but success is false
                setError(result.message || response.statusText || 'Failed to delete investor');
            }
        } catch (error) {
            console.error('Error deleting investor:', error);
            setError('An error occurred while deleting the investor');
        } finally {
            setIsDeleteModalOpen(false);
            setInvestorToDelete(null);
        }
    };

    // Handle download reports
    const handleDownloadReports = () => {
        // Implement report download functionality
        console.log('Downloading investor reports...');
    };

    return (
        <Box m="20px">
            <Header title="INVESTOR LISTINGS" subtitle={`Manage Investor Listings (${totalRecords} total)`} />

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ backgroundColor: '#FFFFFF' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Investor Name</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Property Type</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>City</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Contact Number</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Created Date</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {investorData.map((investor) => (
                                <TableRow key={investor.Investor_ID}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Investor_Name}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Property_Type}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.City}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{investor.Investor_Contact_Number}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{new Date(investor.IsCreated).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            <IconButton 
                                                onClick={() => openViewModal(investor)}
                                                sx={{ 
                                                    color: '#000000',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5'
                                                    }
                                                }}
                                            >
                                                <VisibilityOutlinedIcon />
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => handleDeleteInvestor(investor)}
                                                sx={{ 
                                                    color: '#D32F2F',
                                                    '&:hover': {
                                                        backgroundColor: '#FFEBEE'
                                                    }
                                                }}
                                            >
                                                <DeleteOutlineOutlinedIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(totalRecords / pageSize)} 
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#000000',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#F5F5F5'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: '#D32F2F',
                                color: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#B71C1C'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-disabled': {
                                color: '#9E9E9E'
                            }
                        }}
                    />
                </Box>
            )}

            {/* View Investor Modal */}
            <Modal open={isViewModalOpen} onClose={closeViewModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: '800px',
                    bgcolor: '#FFFFFF',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    {selectedInvestor && (
                        <>
                            <Typography variant="h6" component="h2" mb={2} color="#000000">
                                Investor Details
                            </Typography>

                            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Investor ID</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_ID}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Investor Name</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_Name}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Type</Typography>
                                    <Typography color="#000000">{selectedInvestor.Property_Type}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">City</Typography>
                                    <Typography color="#000000">{selectedInvestor.City}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Contact Number</Typography>
                                    <Typography color="#000000">{selectedInvestor.Investor_Contact_Number}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Created Date</Typography>
                                    <Typography color="#000000">{new Date(selectedInvestor.IsCreated).toLocaleDateString()}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="#000000">Last Updated</Typography>
                                    <Typography color="#000000">{new Date(selectedInvestor.IsUpdated).toLocaleDateString()}</Typography>
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button 
                                    variant="contained" 
                                    onClick={closeViewModal}
                                    sx={{
                                        backgroundColor: '#D32F2F',
                                        '&:hover': {
                                            backgroundColor: '#B71C1C'
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                aria-labelledby="delete-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: colors.primary[400],
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px'
                }}>
                    <Typography variant="h6" component="h2" mb={2}>
                        Confirm Delete
                    </Typography>
                    <Typography mb={3}>
                        Are you sure you want to delete this investor listing? This action cannot be undone.
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            onClick={() => setIsDeleteModalOpen(false)}
                            variant="outlined"
                            sx={{
                                color: colors.grey[100],
                                borderColor: colors.grey[100],
                                '&:hover': {
                                    borderColor: colors.grey[100],
                                    backgroundColor: colors.grey[700]
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            sx={{
                                backgroundColor: colors.redAccent[500],
                                '&:hover': {
                                    backgroundColor: colors.redAccent[700]
                                }
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default InvestListingPage; 